/**
 * FLESH TEXTURES — procedural skin detail for the mega_chibre realism pass.
 * Three families, each an albedo (near-white: the tone lives in vertex
 * colours, like the map families), a normal map from a dedicated height field
 * and a roughness map, all seamlessly tiling (every feature is drawn wrapped):
 *
 *  - `skin`   : pores, the rhombic micro-relief every patch of human skin has,
 *               a few long creases, faint capillaries and freckles;
 *  - `mucosa` : smoother, glossier, with fine papillae speckle and a wet,
 *               lower roughness (the glans / corona);
 *  - `rugae`  : the deep wavy transverse folds of scrotal skin, follicles.
 *
 * Built once per page (module cache), flagged `userData.shared` so the map
 * teardown never disposes them.
 */
import * as THREE from "three";
import { normalMapFrom } from "./mapTextures.js";
import { rng } from "./fleshShell.js";

export type FleshFamily = "skin" | "mucosa" | "rugae";
export interface FleshTex { map: THREE.Texture; normal: THREE.Texture; rough: THREE.Texture }

const SIZE = 512;

function canvas(size = SIZE): { c: HTMLCanvasElement; g: CanvasRenderingContext2D } {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  return { c, g: c.getContext("2d")! };
}
/** draw f at (x,y) and at its wrapped copies so the tile is seamless */
function wrap(x: number, y: number, r: number, f: (x: number, y: number) => void): void {
  for (const ox of [-SIZE, 0, SIZE]) for (const oy of [-SIZE, 0, SIZE]) {
    const X = x + ox, Y = y + oy;
    if (X + r < 0 || X - r > SIZE || Y + r < 0 || Y - r > SIZE) continue;
    f(X, Y);
  }
}
function dot(g: CanvasRenderingContext2D, x: number, y: number, r: number, style: string): void {
  wrap(x, y, r, (X, Y) => { g.fillStyle = style; g.beginPath(); g.arc(X, Y, r, 0, Math.PI * 2); g.fill(); });
}
function blotch(g: CanvasRenderingContext2D, x: number, y: number, r: number, rgb: string, a: number): void {
  wrap(x, y, r, (X, Y) => {
    const gr = g.createRadialGradient(X, Y, 0, X, Y, r);
    gr.addColorStop(0, `rgba(${rgb},${a})`);
    gr.addColorStop(1, `rgba(${rgb},0)`);
    g.fillStyle = gr; g.fillRect(X - r, Y - r, r * 2, r * 2);
  });
}
/** a wobbly polyline from (x,y) along heading, wrapped */
function line(g: CanvasRenderingContext2D, R: () => number, x: number, y: number, hd: number, len: number, step: number, wob: number, width: number, style: string): void {
  const pts: [number, number][] = [[x, y]];
  for (let s = 0; s < len; s += step) {
    hd += (R() - 0.5) * wob;
    x += Math.cos(hd) * step; y += Math.sin(hd) * step;
    pts.push([x, y]);
  }
  wrap(pts[0][0], pts[0][1], len + 4, (X, Y) => {
    const dx = X - pts[0][0], dy = Y - pts[0][1];
    g.strokeStyle = style; g.lineWidth = width; g.lineCap = "round"; g.lineJoin = "round";
    g.beginPath();
    g.moveTo(pts[0][0] + dx, pts[0][1] + dy);
    for (const p of pts) g.lineTo(p[0] + dx, p[1] + dy);
    g.stroke();
  });
}

interface Fields { albedo: HTMLCanvasElement; height: HTMLCanvasElement; rough: HTMLCanvasElement; bump: number }

function makeSkin(lowSpec: boolean): Fields {
  const R = rng(0x5e1f);
  const A = canvas(), H = canvas(), Q = canvas();
  A.g.fillStyle = "#eee6e2"; A.g.fillRect(0, 0, SIZE, SIZE);
  H.g.fillStyle = "#808080"; H.g.fillRect(0, 0, SIZE, SIZE);
  Q.g.fillStyle = "#8c8c8c"; Q.g.fillRect(0, 0, SIZE, SIZE);
  // tonal mottling (subsurface blood, uneven pigment)
  for (let i = 0; i < 60; i++) blotch(A.g, R() * SIZE, R() * SIZE, 30 + R() * 90, R() < 0.6 ? "205,120,120" : "170,130,110", 0.08 + R() * 0.1);
  for (let i = 0; i < 40; i++) blotch(H.g, R() * SIZE, R() * SIZE, 40 + R() * 100, R() < 0.5 ? "150,150,150" : "100,100,100", 0.25);
  // rhombic micro-relief: short grooves in two dominant directions
  const n = lowSpec ? 900 : 1800;
  for (let i = 0; i < n; i++) {
    const hd = (R() < 0.5 ? 0.6 : 2.4) + (R() - 0.5) * 0.5;
    const x = R() * SIZE, y = R() * SIZE, len = 8 + R() * 22;
    line(H.g, R, x, y, hd, len, 3, 0.3, 1.1, "rgba(60,60,60,0.55)");
    line(A.g, R, x, y, hd, len, 3, 0.3, 1, "rgba(150,100,95,0.10)");
  }
  // long creases
  for (let i = 0; i < 9; i++) {
    const x = R() * SIZE, y = R() * SIZE, hd = R() * Math.PI, len = 120 + R() * 220;
    line(H.g, R, x, y, hd, len, 5, 0.25, 2.4, "rgba(30,30,30,0.6)");
    line(A.g, R, x, y, hd, len, 5, 0.25, 2, "rgba(130,80,80,0.14)");
    line(Q.g, R, x, y, hd, len, 5, 0.25, 3, "rgba(200,200,200,0.5)");
  }
  // capillaries
  for (let i = 0; i < 26; i++) line(A.g, R, R() * SIZE, R() * SIZE, R() * 6.3, 30 + R() * 70, 3, 0.8, 0.8, "rgba(170,60,80,0.22)");
  // pores (dark pit with a lighter rim in height), freckles
  const pores = lowSpec ? 2200 : 4200;
  for (let i = 0; i < pores; i++) {
    const x = R() * SIZE, y = R() * SIZE, r = 0.7 + R() * 1.1;
    dot(H.g, x + 0.6, y + 0.6, r + 0.7, "rgba(170,170,170,0.4)");
    dot(H.g, x, y, r, "rgba(25,25,25,0.85)");
    dot(A.g, x, y, r * 0.9, "rgba(140,95,90,0.28)");
    dot(Q.g, x, y, r, "rgba(230,230,230,0.6)");
  }
  for (let i = 0; i < 160; i++) dot(A.g, R() * SIZE, R() * SIZE, 1 + R() * 2.2, `rgba(${120 + R() * 40 | 0},80,60,${0.15 + R() * 0.2})`);
  return { albedo: A.c, height: H.c, rough: Q.c, bump: 2.2 };
}

function makeMucosa(lowSpec: boolean): Fields {
  const R = rng(0x91a5);
  const A = canvas(), H = canvas(), Q = canvas();
  A.g.fillStyle = "#f2e2e4"; A.g.fillRect(0, 0, SIZE, SIZE);
  H.g.fillStyle = "#808080"; H.g.fillRect(0, 0, SIZE, SIZE);
  Q.g.fillStyle = "#4a4a4a"; Q.g.fillRect(0, 0, SIZE, SIZE); // wet: glossy
  for (let i = 0; i < 70; i++) blotch(A.g, R() * SIZE, R() * SIZE, 25 + R() * 80, R() < 0.5 ? "200,90,120" : "150,80,130", 0.1 + R() * 0.12);
  for (let i = 0; i < 50; i++) blotch(H.g, R() * SIZE, R() * SIZE, 30 + R() * 70, R() < 0.5 ? "140,140,140" : "110,110,110", 0.3);
  // drier patches (rougher)
  for (let i = 0; i < 24; i++) blotch(Q.g, R() * SIZE, R() * SIZE, 30 + R() * 60, "150,150,150", 0.45);
  // papillae: dense fine bumps
  const pap = lowSpec ? 3000 : 6500;
  for (let i = 0; i < pap; i++) {
    const x = R() * SIZE, y = R() * SIZE, r = 0.6 + R() * 0.9;
    dot(H.g, x, y, r, "rgba(200,200,200,0.5)");
    if (R() < 0.3) dot(A.g, x, y, r, "rgba(190,90,110,0.18)");
  }
  // fine capillary lacework
  for (let i = 0; i < 60; i++) line(A.g, R, R() * SIZE, R() * SIZE, R() * 6.3, 20 + R() * 60, 2.5, 1.0, 0.7, "rgba(160,40,80,0.18)");
  // a few soft folds
  for (let i = 0; i < 6; i++) {
    const x = R() * SIZE, y = R() * SIZE, hd = R() * Math.PI, len = 100 + R() * 200;
    line(H.g, R, x, y, hd, len, 6, 0.2, 3, "rgba(60,60,60,0.4)");
  }
  return { albedo: A.c, height: H.c, rough: Q.c, bump: 1.4 };
}

function makeRugae(lowSpec: boolean): Fields {
  const R = rng(0x77c3);
  const A = canvas(), H = canvas(), Q = canvas();
  A.g.fillStyle = "#e6d8d2"; A.g.fillRect(0, 0, SIZE, SIZE);
  H.g.fillStyle = "#909090"; H.g.fillRect(0, 0, SIZE, SIZE);
  Q.g.fillStyle = "#7a7a7a"; Q.g.fillRect(0, 0, SIZE, SIZE);
  for (let i = 0; i < 55; i++) blotch(A.g, R() * SIZE, R() * SIZE, 30 + R() * 90, R() < 0.5 ? "150,95,85" : "185,110,110", 0.12 + R() * 0.12);
  // transverse folds: wavy grooves roughly along x, ~22 px apart, wrapped in y
  const folds = 24;
  for (let k = 0; k < folds; k++) {
    const y0 = (k / folds) * SIZE + (R() - 0.5) * 8;
    const segs = 3 + (R() * 3 | 0);
    let x = R() * SIZE;
    for (let s = 0; s < segs; s++) {
      const len = 70 + R() * 150;
      line(H.g, R, x, y0 + (R() - 0.5) * 10, (R() - 0.5) * 0.4, len, 4, 0.35, 5 + R() * 3, "rgba(30,30,30,0.55)");
      line(H.g, R, x, y0 + 7, (R() - 0.5) * 0.4, len, 4, 0.35, 4, "rgba(220,220,220,0.25)");
      line(A.g, R, x, y0, (R() - 0.5) * 0.4, len, 4, 0.35, 4, "rgba(110,60,55,0.18)");
      line(Q.g, R, x, y0, (R() - 0.5) * 0.4, len, 4, 0.35, 5, "rgba(210,210,210,0.35)");
      x += len + R() * 40;
    }
  }
  // follicles + pores
  const n = lowSpec ? 700 : 1500;
  for (let i = 0; i < n; i++) {
    const x = R() * SIZE, y = R() * SIZE, r = 0.9 + R() * 1.3;
    dot(H.g, x, y, r + 1.2, "rgba(190,190,190,0.35)");
    dot(H.g, x, y, r * 0.6, "rgba(20,20,20,0.8)");
    dot(A.g, x, y, r * 0.7, "rgba(90,55,45,0.35)");
  }
  return { albedo: A.c, height: H.c, rough: Q.c, bump: 2.6 };
}

const cache = new Map<string, FleshTex>();
export function fleshTextures(f: FleshFamily, lowSpec: boolean): FleshTex {
  const key = `${f}|${lowSpec ? 1 : 0}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const fl = f === "skin" ? makeSkin(lowSpec) : f === "mucosa" ? makeMucosa(lowSpec) : makeRugae(lowSpec);
  const mk = (c: HTMLCanvasElement, srgb: boolean): THREE.CanvasTexture => {
    const t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    t.anisotropy = 8;
    t.generateMipmaps = true;
    t.userData.shared = true;
    return t;
  };
  const out: FleshTex = {
    map: mk(fl.albedo, true),
    normal: mk(normalMapFrom(fl.height, fl.bump, lowSpec ? 256 : SIZE), false),
    rough: mk(fl.rough, false),
  };
  cache.set(key, out);
  return out;
}
